'use client'

import type { Route } from 'next'
import { useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuthModal } from './auth-modal-provider'

interface AuthRedirectProps {
  /** Where to replace the URL to once the modal opens. Defaults to "/". */
  target?: Route
}

/**
 * Client glue used by legacy `/login` and `/signup` page routes. Mounts,
 * opens the unified auth modal, then replaces the current URL with
 * `target` so the user sees the modal layered on top of the homepage
 * instead of an empty page chrome behind it.
 *
 * Preserves any query params the legacy route received so future modal
 * logic (e.g. `?redirect=/account/billing`, `?intent=batch_trial&shortId=...`)
 * can still read them after the redirect. Without this, a link like
 * `/login?redirect=/account/billing` silently lost its redirect target
 * the moment the modal mounted.
 *
 * Renders `null` so it contributes nothing to the visible tree.
 */
export function AuthRedirect({ target = '/' }: AuthRedirectProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { open } = useAuthModal()

  useEffect(() => {
    open()
    const qs = searchParams.toString()
    router.replace(qs ? `${target}?${qs}` : target)
  }, [open, router, searchParams, target])

  return null
}
